import React, { useState, useEffect, useContext, useCallback } from "react";
import {
	Modal,
	Box,
	Typography,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Alert,
	Button,
} from "@mui/material";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import FormConfig from "../Config/FormConfig";
import { GridEdit } from "../Config/GridEdit";
import { obtenerDatosSites } from "../../api/config";
import { GlobalContext } from "../../GlobalContext";
import { validateField } from "../../Labels/descriptions/LabelsDescriptionsRestrictions/Restrictions";

export default function ModalEdit({ open, onClose, contractData, handleDataUpdate }) {
	const { site_id } = useContext(GlobalContext);
	const { t } = useTranslation();

	// — pasos, carga y select tipo contrato —
	const [paso, setPaso] = useState(1);
	const [loading, setLoading] = useState(true);
	const [tipoContratos, setTipoContratos] = useState([]);
	const [tipoContratoSeleccionado, setTipoContratoSeleccionado] = useState("");
	const [errorSelectTipoContrato, setErrorSelectTipoContrato] = useState(false);

	// — campos generales y validaciones —
	const [changedFields, setChangedFields] = useState({});
	const [errors, setErrors] = useState({});
	const [formError, setFormError] = useState("");

	const fieldConfigs = {
		[t("lab_tool.contract.labels.nombre")]: { minLength: 3, regex: /^[\p{L}\d ]+$/u },
		[t("lab_tool.contract.labels.kronos_name")]: { minLength: 3, regex: /^[\p{L}\d ]+$/u },
		[t("lab_tool.contract.labels.productividad")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.rotacion")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.zcero")]: { type: "number", allowDecimal: false },
		[t("lab_tool.contract.labels.cotsup")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.cotinf")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.ausentismo")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.contrato")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.despido")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.salario")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.hora_contrato")]: { type: "number", allowDecimal: true },
		[t("lab_tool.contract.labels.dias_lab_maximos")]: { type: "number", allowDecimal: false },
		[t("lab_tool.contract.labels.dias_lab_minimos")]: { type: "number", allowDecimal: false },
		[t("lab_tool.contract.labels.contrato_extra")]: { type: "checkbox" },
		[t("lab_tool.contract.labels.meses_contrato")]: {
			type: "text",
			allowDecimal: false,
			dependsOn: t("lab_tool.contract.labels.contrato_extra"),
			dependsValue: "true",
		},
	};
	const tooltips = [
		t("lab_tool.contract.tooltips.nombre"),
		t("lab_tool.contract.tooltips.kronos_name"),
		t("lab_tool.contract.tooltips.productividad"),
		t("lab_tool.contract.tooltips.rotacion"),
		t("lab_tool.contract.tooltips.ausentismo"),
		t("lab_tool.contract.tooltips.cotsup"),
		t("lab_tool.contract.tooltips.cotinf"),
		t("lab_tool.contract.tooltips.dias_lab_maximos"),
		t("lab_tool.contract.tooltips.dias_lab_minimos"),
		t("lab_tool.contract.tooltips.contrato"),
		t("lab_tool.contract.tooltips.despido"),
		t("lab_tool.contract.tooltips.salario"),
		t("lab_tool.contract.tooltips.zcero"),
		t("lab_tool.contract.tooltips.hora_contrato"),
		t("lab_tool.contract.tooltips.meses_contrato"),
		t("lab_tool.contract.tooltips.contrato_extra"),
	];

	// — calendario: se actualiza desde GridEdit —
	const [schedule, setSchedule] = useState({ days: [], periods: [] });
	const handleScheduleChange = useCallback((days, periods) => {
		setSchedule({ days, periods });
		setFormError("");
	}, []);

	// — prepara renameData para FormConfig —
	const renameData = React.useMemo(() => {
		if (!contractData) return {};
		return {
			[t("lab_tool.contract.labels.nombre")]: contractData.nombre,
			[t("lab_tool.contract.labels.kronos_name")]: contractData.kronos_name,
			[t("lab_tool.contract.labels.productividad")]: contractData.productividad,
			[t("lab_tool.contract.labels.rotacion")]: contractData.rotacion,
			[t("lab_tool.contract.labels.ausentismo")]: contractData.ausentismo,
			[t("lab_tool.contract.labels.cotsup")]: contractData.cotsup,
			[t("lab_tool.contract.labels.cotinf")]: contractData.cotinf,
			[t("lab_tool.contract.labels.dias_lab_maximos")]: contractData.dias_lab_maximos,
			[t("lab_tool.contract.labels.dias_lab_minimos")]: contractData.dias_lab_minimos,
			[t("lab_tool.contract.labels.contrato")]: contractData.contrato,
			[t("lab_tool.contract.labels.despido")]: contractData.despido,
			[t("lab_tool.contract.labels.salario")]: contractData.salario,
			[t("lab_tool.contract.labels.zcero")]: contractData.zcero,
			[t("lab_tool.contract.labels.hora_contrato")]: contractData.hora_contrato,
			[t("lab_tool.contract.labels.contrato_extra")]:
				contractData.contrato_extra === 1 ? "true" : "false",
			[t("lab_tool.contract.labels.meses_contrato")]:
				contractData.contrato_extra === 1 ? String(contractData.meses_contrato ?? "0") : "0",
		};
	}, [contractData, t]);

	// — carga inicial de todo cuando abro el modal o cambia el contrato —
	useEffect(() => {
		if (!open || !contractData) return;
		setLoading(true);
		// 1) reset general
		setPaso(1);
		setChangedFields(renameData);
		setErrors({});
		setFormError("");
		setTipoContratoSeleccionado(contractData.id_tipo_contrato || "");
		setErrorSelectTipoContrato(false);
		// 2) tipocontrato
		obtenerDatosSites(`/tipo_contrato/get?site_id=${site_id}`)
			.then((resp) => {
				setTipoContratos(resp);
				if (!resp.some((x) => x.id === contractData.id_tipo_contrato)) {
					setTipoContratoSeleccionado("");
				}
			})
			.catch(console.error)
			.finally(() => setLoading(false));
		// 3) inicializo schedule
		const days = contractData.contracts_per_day.map((d) => ({
			config_contracts_per_days_id: d.config_contracts_per_days_id,
			contract_id: contractData.id,
			day: d.day,
			value: d.value,
		}));
		const periods = contractData.contracts_per_periods.map((p) => ({
			config_contracts_per_days_id: p.config_contracts_per_days_id,
			config_contracts_per_periods_id: p.config_contracts_per_periods_id,
			periods: p.periods.map(({ start_time, end_time }) => ({ start_time, end_time })),
		}));
		setSchedule({ days, periods });
	}, [open, contractData, site_id, renameData]);

	// — manejadores de campos generales —
	const handleFieldChange = (label, value) => {
		setChangedFields((prev) => {
			const upd = { ...prev, [label]: value };
			setErrors((errs) => ({
				...errs,
				[label]: validateField(label, value, upd, t),
			}));
			return upd;
		});
	};

	// — al presionar “Actualizar” valida todo de nuevo y envia payload —
	const handleButtonClick = async () => {
		// validaciones de schedule
		const allDaysEmpty = schedule.days.every((d) => d.value === 0);
		const anyEmptyDay = schedule.days.some((d) => {
			if (d.value === 0) return false;
			const pd = schedule.periods.find(
				(p) => p.config_contracts_per_days_id === d.config_contracts_per_days_id
			);
			return !pd || pd.periods.length === 0;
		});
		const anyIncomplete = schedule.periods.some((p) =>
			p.periods.some((x) => !x.start_time || !x.end_time)
		);

		if (allDaysEmpty) return setFormError(t("aux.messages.select_day"));
		if (anyEmptyDay) return setFormError("Hay días seleccionados sin periodos asociados.");
		if (anyIncomplete)
			return setFormError("Existen periodos incompletos. Completa o elimina los pares vacíos.");
		if (!tipoContratoSeleccionado) {
			setErrorSelectTipoContrato(true);
			return;
		}

		// Armo payload
		const payload = {
			id: contractData.id,
			config_site_id: site_id,
			nombre: changedFields[t("lab_tool.contract.labels.nombre")],
			kronos_name: changedFields[t("lab_tool.contract.labels.kronos_name")],
			productividad: changedFields[t("lab_tool.contract.labels.productividad")],
			rotacion: changedFields[t("lab_tool.contract.labels.rotacion")],
			ausentismo: changedFields[t("lab_tool.contract.labels.ausentismo")],
			cotsup: changedFields[t("lab_tool.contract.labels.cotsup")],
			cotinf: changedFields[t("lab_tool.contract.labels.cotinf")],
			dias_lab_maximos: changedFields[t("lab_tool.contract.labels.dias_lab_maximos")],
			dias_lab_minimos: changedFields[t("lab_tool.contract.labels.dias_lab_minimos")],
			contrato: changedFields[t("lab_tool.contract.labels.contrato")],
			despido: changedFields[t("lab_tool.contract.labels.despido")],
			salario: changedFields[t("lab_tool.contract.labels.salario")],
			zcero: changedFields[t("lab_tool.contract.labels.zcero")],
			hora_contrato: changedFields[t("lab_tool.contract.labels.hora_contrato")],
			contrato_extra: changedFields[t("lab_tool.contract.labels.contrato_extra")],
			meses_contrato:
				changedFields[t("lab_tool.contract.labels.contrato_extra")] === "true"
					? changedFields[t("lab_tool.contract.labels.meses_contrato")]
					: "0",
			id_tipo_contrato: tipoContratoSeleccionado,
			contracts_per_day: schedule.days,
			contracts_per_periods: schedule.periods,
		};

		handleDataUpdate(payload);
		onClose();
		try {
			await Swal.fire({
				icon: "success",
				title: t("aux.messages.alert.success"),
				text: t("aux.messages.alert.text_alert.update"),
			});
			onClose();
		} catch {
			await Swal.fire({
				icon: "error",
				title: t("aux.messages.alert.error"),
				text: t("aux.messages.alert.text_alert.error_update"),
			});
		}
	};

	if (loading) {
		return (
			<Modal
				open={open}
				onClose={onClose}
				sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
			>
				<CircularProgress />
			</Modal>
		);
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
		>
			<Box
				sx={{
					width: { xs: "95%", sm: "80%", md: "60%" },
					maxHeight: "90vh",
					bgcolor: "background.paper",
					p: 2,
					overflowY: "auto",
				}}
			>
				{paso === 1 ? (
					<>
						<Typography variant="h6" gutterBottom>
							{t("aux.step")} 1 {t("aux.of")} 2 – {t("config.config_general")}
						</Typography>
						<FormControl fullWidth required error={errorSelectTipoContrato} sx={{ mb: 2 }}>
							<InputLabel>{t("aux.type_contract")}</InputLabel>
							<Select
								labelId="tipo-contrato-label"
								value={tipoContratoSeleccionado || ""}
								label={t("aux.type_contract")}
								displayEmpty
								onChange={(e) => {
									setTipoContratoSeleccionado(e.target.value);
									e.target.value && setErrorSelectTipoContrato(false);
								}}
							>
								<MenuItem value="" disabled>
									<em>{t("aux.select_contract_placeholder")}</em>
								</MenuItem>
								{tipoContratos.map((tc) => (
									<MenuItem key={tc.id} value={tc.id}>
										{tc.nombre}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<FormConfig
							labels={Object.keys(renameData)}
							valorDefault={changedFields}
							handleFieldChange={handleFieldChange}
							tooltips={tooltips}
							errors={errors}
							fieldConfigs={fieldConfigs}
						/>

						{formError && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{formError}
							</Alert>
						)}

						<Button fullWidth variant="contained" onClick={() => setPaso(2)}>
							{t("aux.buttons.continue")}
						</Button>
					</>
				) : (
					<>
						<Typography variant="h6" gutterBottom>
							{t("aux.step")} 2 {t("aux.of")} 2 – {t("config.contract.title")}
						</Typography>
						<GridEdit
							idContract={contractData.id}
							dataDays={schedule.days}
							dataPeriods={schedule.periods}
							onDataUpdate={handleScheduleChange}
						/>

						{formError && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{formError}
							</Alert>
						)}

						<Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
							<Button onClick={() => setPaso(1)}>{t("aux.buttons.back")}</Button>
							<Button variant="contained" onClick={handleButtonClick}>
								{t("aux.buttons.update")}
							</Button>
						</Box>
					</>
				)}
			</Box>
		</Modal>
	);
}
